import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useRef, useContext } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalExpiryHandler from './components/Global/GlobalExpiryHandler';
import { AuthContext } from './components/Auth/AuthContext';
import Loader from './utils/Loader';
import { Agentation } from 'agentation';
// Development utilities
if (process.env.NODE_ENV === 'development') {
  import('./utils/testExpiry');
}

// ───── lazy imports ─────────────────────────────────────────────────────────────
const Login               = lazy(() => import('./pages/Login'));
const ForgotPassword      = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword       = lazy(() => import('./pages/ResetPassword'));
const AuthCallback        = lazy(() => import('./pages/AuthCallback'));
const TermsAndConditions  = lazy(() => import('./pages/legal/TermsAndConditions'));
const AdminRoutes         = lazy(() => import('./Routes/AdminRoutes'));
const SupervisorRoutes    = lazy(() => import('./Routes/SupervisorRoutes'));
const AgentRoutes         = lazy(() => import('./Routes/AgentRoutes'));
const SoftphoneStandalone = lazy(() => import('./pages/WebRTC/SoftphoneStandalone'));
const TicketFeedback      = lazy(() => import('./pages/Support/TicketFeedback'));


export default function App() {
  // Force logout is handled via WebSocket in NotificationContext (no polling needed)

  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasToken, loading, authChecked, setCurrentPath } = useContext(AuthContext);
  
  const navigationRef = useRef(false);
  const userRole = sessionStorage.getItem('user_role');
  const isLoggedIn = hasToken && user;

  // Save current path and handle navigation
  useEffect(() => {
    const path = location.pathname;
    setCurrentPath(path);

    if (isLoggedIn && userRole && location.pathname === '/' && !navigationRef.current) {
      navigationRef.current = true;
      const roleRoutes = {
        admin: '/admin/dashboard',
        supervisor: '/supervisor/dashboard',
        agent: '/agent/dashboard',
      };
      
      const targetRoute = roleRoutes[userRole];
      if (targetRoute) {
        navigate(targetRoute, { replace: true });
      }
    }
    
    if (!isLoggedIn) {
      navigationRef.current = false;
    }
  }, [isLoggedIn, userRole, location.pathname, navigate, setCurrentPath]);

  // Show loading while auth is being checked
  if (!authChecked || loading) {
    return <Loader />;
  }



  return (
    <ErrorBoundary>
      <GlobalExpiryHandler />
      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        hideProgressBar
        closeOnClick
        pauseOnHover
        closeButton={false}
        toastClassName="!bg-white dark:!bg-gray-800 !text-primary-black dark:!text-white !rounded-xl !shadow-lg !border !border-stroke dark:!border-gray-700 !px-4 !py-3 !text-sm !font-medium"
        bodyClassName="!p-0"
      />
      <Suspense fallback={<Loader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/feedback/:token" element={<TicketFeedback />} />
        
        {/* Standalone routes without authentication */}
        <Route path="/agent/softphone-standalone" element={<SoftphoneStandalone />} />
        
        {!isLoggedIn ? (
          <>
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : (
          <>
            {userRole === 'admin' && <Route path="/admin/*" element={<AdminRoutes />} />}
            {userRole === 'supervisor' && <Route path="/supervisor/*" element={<SupervisorRoutes />} />}
            {userRole === 'agent' && <Route path="/agent/*" element={<AgentRoutes />} />}
            
            <Route path="*" element={<Navigate to={userRole ? `/${userRole}/dashboard` : '/'} replace />} />
          </>
        )}
      </Routes>
      </Suspense>
      {process.env.NODE_ENV === 'development' && <Agentation endpoint="http://localhost:4747" />}
    </ErrorBoundary>
  );
}
